import { Avatar, Box, Chip, Divider, Grid, Paper, Stack, Typography, Button, TextField, Snackbar, Alert, CircularProgress, Accordion, AccordionSummary, AccordionDetails, FormControl, Select, MenuItem, InputLabel } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Headeer from "../../components/Headeer";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import DoctorService from "../../services/doctorService";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import Groups2Icon from "@mui/icons-material/Groups2";
import EditIcon from "@mui/icons-material/Edit";
import SecurityIcon from "@mui/icons-material/Security";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PublicIcon from "@mui/icons-material/Public";
import { COUNTRIES } from "../../utils/countries";

export default function DoctorProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('doctorProfile');
  const doctorService = new DoctorService();
  const [doctorInfo, setDoctorInfo] = useState({});
  const [permissions, setPermissions] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedDoctor, setEditedDoctor] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const response = await doctorService.getDoctorInfo()
        console.log('🔍 Doctor Profile - Full response:', response);
        console.log('🔍 Doctor Profile - Doctor data:', response?.doctor);
        console.log('🔍 Doctor Profile - Nationality:', response?.doctor?.nationality);
        setDoctorInfo(response);
        // Permissions bilgisini ayır ve sakla
        if (response?.permissions) {
          console.log('🔍 Doctor Profile - Permissions received:', response.permissions);
          console.log('🔍 Permissions type:', typeof response.permissions);
          console.log('🔍 Permissions keys:', Object.keys(response.permissions));
          console.log('🔍 True permissions:', Object.keys(response.permissions).filter(k => {
            const val = response.permissions[k];
            return val === true || val === 'true' || val === 1;
          }));
          setPermissions(response.permissions);
        } else {
          // Permissions yoksa boş objesi set et
          console.warn('⚠️ No permissions in response:', response);
          setPermissions({});
        }
      }  catch (error) {
        console.error("Bir sorun oluştu", error);
        setSnackbar({
          open: true,
          message: t('errorFetchingDoctorInfo'),
          severity: "error",
        });
      }
    };
    fetchDoctorInfo();
  },[]);

  const doctor = doctorInfo?.doctor || {};
  const subDoctorCount = doctorInfo?.subDoctors?.length ?? 0;
  const initials = `${(doctor.name?.[0] || "D").toUpperCase()}${(doctor.surname?.[0] || "R").toUpperCase()}`;

  const capitalizeWords = (str) => {
    return str.trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleEditMode = () => {
    setIsEditMode(true);
    setEditedDoctor({
      name: doctor.name || "",
      surname: doctor.surname || "",
      specialization: doctor.specialization || "",
      phone_number: doctor.phone_number || "",
      nationality: doctor.nationality || "",
    });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedDoctor({});
  };

  const handleSaveDoctor = async () => {
    try {
      setLoading(true);
      const doctorId = doctor.id || JSON.parse(localStorage.getItem("user") || "{}").id;
      
      if (!doctorId) {
        setSnackbar({
          open: true,
          message: t('doctorIdNotFound'),
          severity: "error",
        });
        setLoading(false);
        return;
      }

      const response = await doctorService.updateDoctor(doctorId, editedDoctor);
      console.log("Doctor information updated:", response);
      
      // Refresh doctor info
      const updatedResponse = await doctorService.getDoctorInfo();
      setDoctorInfo(updatedResponse);
      
      // Eğer nationality güncellendiyse, localStorage'ı ve dili güncelle
      if (editedDoctor.nationality !== undefined) {
        // localStorage'daki user bilgisini güncelle
        const userStr = localStorage.getItem("user");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            user.nationality = editedDoctor.nationality;
            localStorage.setItem("user", JSON.stringify(user));
            
            // Nationality'ye göre dil ayarla (nationality değiştiği için force=true)
            const { setLanguageFromNationality } = await import("../../utils/languageFromNationality");
            setLanguageFromNationality(editedDoctor.nationality, true);
          } catch (e) {
            console.error("Error updating user nationality:", e);
          }
        }
      }
      
      setIsEditMode(false);
      setEditedDoctor({});
      setSnackbar({
        open: true,
        message: t('doctorInfoUpdatedSuccessfully'),
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating doctor:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('errorUpdatingDoctorInfo'),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedDoctor({
      ...editedDoctor,
      [field]: value
    });
  };

  const infoItems = useMemo(() => [
    {
      label: t('name'),
      value: isEditMode ? (
        <TextField
          fullWidth
          variant="filled"
          value={editedDoctor.name || ""}
          onChange={(e) => handleInputChange("name", e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
          }}
        />
      ) : (
        doctor.name || "—"
      ),
      icon: <PersonOutlineIcon fontSize="small" />,
    },
    {
      label: t('surname'),
      value: isEditMode ? (
        <TextField
          fullWidth
          variant="filled"
          value={editedDoctor.surname || ""}
          onChange={(e) => handleInputChange("surname", e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
          }}
        />
      ) : (
        doctor.surname || "—"
      ),
      icon: <PersonOutlineIcon fontSize="small" />,
    },
    {
      label: t('email'),
      value: doctor.email || "—",
      icon: <MailOutlineIcon fontSize="small" />,
      readOnly: true,
    },
    {
      label: t('phoneNumber'),
      value: isEditMode ? (
        <TextField
          fullWidth
          variant="filled"
          value={editedDoctor.phone_number || ""}
          onChange={(e) => handleInputChange("phone_number", e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
          }}
        />
      ) : (
        doctor.phone_number || "—"
      ),
      icon: <PhoneOutlinedIcon fontSize="small" />,
    },
    {
      label: t('specialization'),
      value: isEditMode ? (
        <TextField
          fullWidth
          variant="filled"
          value={editedDoctor.specialization || ""}
          onChange={(e) => handleInputChange("specialization", e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
          }}
        />
      ) : (
        doctor.specialization || "—"
      ),
      icon: <LocalHospitalIcon fontSize="small" />,
    },
    {
      label: t('nationality') || "Nationality",
      value: isEditMode ? (
        <FormControl
          fullWidth
          variant="filled"
        >
          <InputLabel sx={{ color: colors.grey[300] }}>{t('nationality') || "Nationality"}</InputLabel>
          <Select
            value={editedDoctor.nationality || ""}
            onChange={(e) => handleInputChange("nationality", e.target.value)}
            label={t('nationality') || "Nationality"}
            IconComponent={ExpandMoreIcon}
            sx={{
              "& .MuiSelect-select": {
                color: editedDoctor.nationality ? colors.grey[100] : colors.grey[400],
              },
              "& .MuiFilledInput-root": {
                backgroundColor: colors.primary[400],
                "&:hover": {
                  backgroundColor: colors.primary[300],
                },
                "&.Mui-focused": {
                  backgroundColor: colors.primary[300],
                },
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: colors.primary[600],
                  "& .MuiMenuItem-root": {
                    color: colors.grey[100],
                    "&:hover": {
                      backgroundColor: colors.greenAccent[700],
                    },
                    "&.Mui-selected": {
                      backgroundColor: colors.greenAccent[800],
                    },
                  },
                },
              },
            }}
          >
            {COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.name}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        doctor.nationality || "—"
      ),
      icon: <PublicIcon fontSize="small" />,
    },
    {
      label: t('role'),
      value: doctor.role || "—",
      icon: <WorkspacePremiumIcon fontSize="small" />,
      readOnly: true,
    },
  ], [isEditMode, editedDoctor, doctor, colors, t]);

  return (
    <Box sx={{ marginLeft: 2, marginRight: 2, py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Headeer title={t('title')} subtitle={t('subtitle')} />
        <Box sx={{ display: "flex", gap: 2 }}>
          {isEditMode ? (
            <>
              <Button
                onClick={handleSaveDoctor}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                sx={{
                  backgroundColor: colors.greenAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: colors.greenAccent[600],
                  },
                  "&:disabled": {
                    backgroundColor: colors.grey[700],
                  },
                }}
              >
                {loading ? t('saving') : t('saveChanges')}
              </Button>
              <Button
                onClick={handleCancelEdit}
                disabled={loading}
                sx={{
                  backgroundColor: colors.redAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: colors.redAccent[600],
                  },
                }}
              >
                {t('cancel')}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleEditMode}
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: "10px 20px",
                "&:hover": {
                  backgroundColor: colors.blueAccent[600],
                },
              }}
            >
              {t('editProfile')}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", px: { xs: 0, md: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 760,
            borderRadius: 4,
            backgroundColor: colors.primary[500],
            overflow: "hidden",
            boxShadow: `0 24px 48px ${colors.primary[900]}60`,
            border: `1px solid ${colors.primary[200]}30`,
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
              px: { xs: 3, md: 4 },
              py: { xs: 4, md: 5 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 3, md: 4 },
            }}
          >
            <Avatar
              sx={{
                width: { xs: 84, md: 96 },
                height: { xs: 84, md: 96 },
                fontSize: 32,
                bgcolor: colors.greenAccent[500],
                color: colors.primary[900],
                border: `3px solid rgba(255, 255, 255, 0.5)`,
                boxShadow: `0 14px 30px rgba(0, 0, 0, 0.35)`
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "white", letterSpacing: 0.4 }}>
                {doctor.name || doctor.surname ? `${doctor.name || ""} ${doctor.surname || ""}`.trim() : t('doctor')}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)", mt: 0.8, fontSize: "1rem" }}>
                {doctor.specialization || t('specializationNotProvided')}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                {doctor.role && (
                  <Chip
                    icon={<WorkspacePremiumIcon fontSize="small" />}
                    label={t('roleLabel', { role: doctor.role })}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "white",
                      letterSpacing: 0.6,
                      fontWeight: 600,
                    }}
                  />
                )}
                <Chip
                  icon={<Groups2Icon fontSize="small" />}
                  label={t('subDoctorCount', { count: subDoctorCount })}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.12)",
                    color: "white",
                    letterSpacing: 0.6,
                    fontWeight: 600,
                  }}
                />
                {doctor.role_title && (
                  <Chip
                    icon={<WorkspacePremiumIcon fontSize="small" />}
                    label={t('titleLabel', { title: doctor.role_title })}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.12)",
                      color: "white",
                      letterSpacing: 0.6,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ borderColor: `${colors.primary[200]}33` }} />

          <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 } }}>
            <Grid container spacing={3}>
              {infoItems.map((item) => (
                <Grid item xs={12} md={6} key={item.label}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      backgroundColor: colors.primary[600],
                      borderRadius: 3,
                      border: `1px solid ${colors.primary[200]}22`,
                      boxShadow: `0 10px 24px ${colors.primary[900]}35`,
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                          color: colors.grey[100],
                          boxShadow: `0 8px 20px ${colors.primary[900]}50`,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.grey[400],
                            letterSpacing: 0.8,
                            textTransform: "uppercase",
                          }}
                        >
                          {item.label}
                        </Typography>
                        {typeof item.value === 'string' ? (
                          <Typography
                            sx={{
                              color: colors.grey[100],
                              fontWeight: 600,
                              fontSize: "1.05rem",
                              mt: 0.5,
                            }}
                          >
                            {item.value}
                          </Typography>
                        ) : (
                          <Box sx={{ mt: 0.5 }}>
                            {item.value}
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Permissions Section */}
          {permissions && (
            <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 }, pt: 0 }}>
              <Accordion
                defaultExpanded={false}
                sx={{
                  backgroundColor: colors.primary[600],
                  border: `1px solid ${colors.primary[200]}22`,
                  borderRadius: 3,
                  boxShadow: `0 10px 24px ${colors.primary[900]}35`,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[100] }} />}
                  sx={{
                    backgroundColor: colors.primary[700],
                    borderRadius: 3,
                    "&:hover": {
                      backgroundColor: colors.primary[650],
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                    <SecurityIcon sx={{ color: colors.blueAccent[400], fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                      {t('myPermissions')}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 3 }}>
                  <PermissionsDisplay permissions={permissions} colors={colors} />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Permissions Display Component
function PermissionsDisplay({ permissions, colors }) {
  const { t } = useTranslation('doctorProfile');
  // Group permissions by resource
  const groupPermissions = (perms) => {
    if (!perms || typeof perms !== 'object') {
      return {};
    }

    const grouped = {};
    
    Object.keys(perms).forEach(key => {
      // More flexible check for true values (handle boolean true, string "true", number 1)
      const value = perms[key];
      const isTrue = value === true || value === 'true' || value === 1 || value === '1';
      
      if (isTrue && key && typeof key === 'string' && key.startsWith('can_')) {
        // Parse permission key: can_own_files_delete -> {resource: 'Own Files', operation: 'Delete'}
        // or can_files_delete -> {resource: 'Files', operation: 'Delete'}
        let resource = '';
        let operation = '';
        
        if (key.startsWith('can_own_')) {
          const withoutPrefix = key.replace('can_own_', '');
          const parts = withoutPrefix.split('_');
          
          // Last part is usually the operation
          operation = parts[parts.length - 1];
          resource = parts.slice(0, -1).join(' ');
          resource = 'Own ' + resource; // Add "Own" prefix for doctor permissions
        } else if (key.startsWith('can_')) {
          const withoutPrefix = key.replace('can_', '');
          const parts = withoutPrefix.split('_');
          
          // Handle special cases like can_patient_files_upload (3 parts: patient, files, upload)
          // or can_permission_groups_create (2 parts: permission_groups, create)
          if (parts.length >= 2) {
            operation = parts[parts.length - 1];
            resource = parts.slice(0, -1).join(' ');
          } else if (parts.length === 1) {
            // Single part - might be just operation
            operation = parts[0];
            resource = '';
          } else {
            // Empty - skip
            return;
          }
        } else {
          // Doesn't start with can_ - skip
          return;
        }
        
        // Skip if resource is empty (should not happen but safety check)
        if (!resource || resource.trim() === '') {
          console.warn('Skipping permission with empty resource:', { key, operation });
          return;
        }
        
        // Capitalize
        resource = resource.replace(/\b\w/g, char => char.toUpperCase());
        operation = operation.charAt(0).toUpperCase() + operation.slice(1);
        
        // Handle special cases
        if (operation === 'Upload') {
          operation = 'Upload';
        }
        
        // Ensure both resource and operation are valid before adding
        if (resource && resource.trim() && operation && operation.trim()) {
          if (!grouped[resource]) {
            grouped[resource] = [];
          }
          if (!grouped[resource].includes(operation)) {
            grouped[resource].push(operation);
          }
        }
      }
    });
    
    console.log('Grouped permissions result:', grouped);
    console.log('Grouped keys count:', Object.keys(grouped).length);
    return grouped;
  };

  if (!permissions || typeof permissions !== 'object') {
    return (
      <Typography sx={{ color: colors.grey[400], textAlign: "center", py: 2 }}>
        {t('noPermissionsDataAvailable')}
      </Typography>
    );
  }

  // More robust permission count check - handle different data types
  const allKeys = permissions && typeof permissions === 'object' && !Array.isArray(permissions) ? Object.keys(permissions) : [];
  const truePermissions = allKeys.filter(k => {
    const value = permissions[k];
    // Check for true in various formats (boolean true, string "true", number 1)
    return value === true || value === 'true' || value === 1 || value === '1';
  });
  const canPermissions = truePermissions.filter(k => k && typeof k === 'string' && k.startsWith('can_'));
  const permissionCount = canPermissions.length;

  const groupedPermissions = groupPermissions(permissions);

  if (permissionCount === 0) {
    return (
      <Typography sx={{ color: colors.grey[400], textAlign: "center", py: 2 }}>
        {t('noPermissionsAssigned')}
      </Typography>
    );
  }
  
  // Check if groupedPermissions has any items
  const groupedKeys = Object.keys(groupedPermissions);
  if (groupedKeys.length === 0) {
    return (
      <Typography sx={{ color: colors.grey[400], textAlign: "center", py: 2 }}>
        {t('noPermissionsAssigned')}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: colors.grey[400], mb: 3 }}>
        {t('totalPermissionsAssigned', { count: permissionCount })}
      </Typography>
      <Grid container spacing={2}>
        {Object.keys(groupedPermissions).map((resource) => (
          <Grid item xs={12} sm={6} md={4} key={resource}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: colors.primary[700],
                borderRadius: 2,
                border: `1px solid ${colors.primary[200]}22`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: colors.grey[200],
                  fontWeight: 600,
                  mb: 1.5,
                  fontSize: "0.95rem",
                }}
              >
                {resource}
              </Typography>
              <Stack spacing={0.5}>
                {groupedPermissions[resource].map((operation) => (
                  <Box
                    key={operation}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        fontSize: 16,
                        color: colors.greenAccent[500],
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.grey[300],
                        fontSize: "0.85rem",
                      }}
                    >
                      {operation}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
